import { TableNames, Doc } from "../../../_generated/dataModel";
import { tableConfigs } from "../../config";
import { isLocalInstallEdition } from "../../config/core";
import type { DomainTableConfig } from "../../config/types";

// Alias for backward compatibility
type TableConfig<T extends TableNames = TableNames> = DomainTableConfig<T>;

// Tipo para las relaciones configuradas
type RelationConfig = {
  key: string;
  tableName: TableNames;
  many?: boolean;
  schema: Record<string, any>;
  sourceField?: string;
};

/**
 * Config neutro para tablas que no existen en la edición actual (ej: on-prem
 * filtra operations/reports a nivel de metadata). Permite que getTableConfig
 * no tire error y que las queries de los módulos joya se compilen, aunque su
 * handler retorne respuesta vacía.
 */
const EMPTY_TABLE_CONFIG: TableConfig = {
  tableName: "" as TableNames,
  schema: {},
  relations: [] as RelationConfig[],
  searchFields: [],
  hasStatusIndex: false,
  customRules: [],
};

/**
 * Tipo que representa un documento enriquecido con sus relaciones
 * Las relaciones se agregan como propiedades opcionales con tipo any
 * ya que la inferencia exacta desde tableConfigs dinamico no es posible.
 */
export type EnrichedDoc<T extends TableNames> = Doc<T> & {
  [key: string]: Doc<TableNames> | Doc<TableNames>[] | undefined;
};

/**
 * Helper para obtener el tipo enriquecido de una tabla específica
 */
export type GetEnrichedDoc<T extends TableNames> = EnrichedDoc<T>;

/**
 * Helper type-safe para obtener configuración de una tabla
 */
export function getTableConfig<T extends TableNames>(
  tableName: T,
): TableConfig {
  const config = (tableConfigs as Record<string, any>)[tableName];
  if (!config) {
    if (isLocalInstallEdition()) {
      return EMPTY_TABLE_CONFIG;
    }
    throw new Error(
      `Table configuration not found for "${tableName}". ` +
        `Add it to tableConfigs in convex/common/tableConfigs.ts`,
    );
  }
  return config;
}

/**
 * Helper para verificar si una tabla tiene configuración
 */
export function hasTableConfig(tableName: TableNames): boolean {
  return tableName in tableConfigs;
}

/**
 * Helper para obtener todas las tablas configuradas
 */
export function getConfiguredTables(): TableNames[] {
  return Object.keys(tableConfigs) as TableNames[];
}

/**
 * Helper para validar que una tabla tenga la configuración mínima requerida
 */
export function validateTableConfig(tableName: TableNames): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!hasTableConfig(tableName)) {
    errors.push(`Table "${tableName}" is not configured`);
    return { isValid: false, errors };
  }

  const config = getTableConfig(tableName);

  if (!config.schema) {
    errors.push(`Table "${tableName}" is missing schema`);
  }

  if (config.searchFields && config.searchFields.length === 0) {
    errors.push(
      `Table "${tableName}" has empty searchFields array (remove it or add fields)`,
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Helper para verificar si una tabla tiene internalControl en su schema
 */
export function hasInternalControl(tableName: TableNames): boolean {
  const config = getTableConfig(tableName);
  return "internalControl" in config.schema.fields;
}

/**
 * Helper para verificar si una tabla tiene campo status en su schema
 */
export function hasStatusField(tableName: TableNames): boolean {
  const config = getTableConfig(tableName);
  return "status" in config.schema.fields;
}

/**
 * Helper para obtener el campo correcto de estado según la tabla
 * Prioridad: 1) campo status si existe, 2) internalControl.state si tiene internalControl
 */
export function getStatusField(tableName: TableNames): string {
  if (hasStatusField(tableName)) {
    return "status";
  }
  if (hasInternalControl(tableName)) {
    return "internalControl.state";
  }
  return "status"; // fallback
}

/**
 * Helper para obtener el nombre del índice de estado según la tabla
 */
export function getStatusIndexName(tableName: TableNames): string {
  if (hasStatusField(tableName)) {
    return "by_status";
  }
  if (hasInternalControl(tableName)) {
    return "by_state";
  }
  return "by_status"; // fallback
}

/**
 * Helper para obtener el nombre del índice combinado company + status según la tabla
 */
export function getCompanyAndStatusIndexName(tableName: TableNames): string {
  if (hasStatusField(tableName)) {
    return "by_company_and_status";
  }
  if (hasInternalControl(tableName)) {
    return "by_company_and_state";
  }
  return "by_company_and_status"; // fallback
}

/**
 * Helper para obtener el campo correcto de companyId según la tabla
 */
export function getCompanyIdField(tableName: TableNames): string {
  return hasInternalControl(tableName)
    ? "internalControl.companyId"
    : "companyId";
}
